import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, Button, CircularProgress, Divider, Link,
} from '@mui/material';
import { DataGrid } from '../../../components/common/datagrid/ResponsiveDataGrid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import * as groupApi from '../../../lib/api/cstGroup';
import GroupFormDialog from '../../../components/chancemng/group/GroupFormDialog';
import { getNumberComma } from '../../../utils/numberUtils';

/** 값 하나짜리 카드 */
const Stat = ({ label, value, warn }) => (
  <Paper variant="outlined" sx={{ p: 1.2, minWidth: 120, flex: 1,
    borderColor: warn ? 'warning.main' : undefined }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" sx={{ color: warn ? 'warning.main' : undefined }}>{value}</Typography>
  </Paper>
);

const GroupDetailContainer = () => {
  const { groupUuid } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await groupApi.getGroup(groupUuid);
      setGroup(res.data?.info || null);
    } catch {
      enqueueSnackbar('묶음을 불러오지 못했습니다.', { variant: 'error' });
      setGroup(null);
    } finally {
      setLoading(false);
    }
  }, [groupUuid, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    // 몇 건의 소속이 풀리는지 먼저 알려 주고 묻는다. 기회 자체는 안 지워진다.
    let count = 0;
    try {
      const r = await groupApi.getGroupChanceCount(groupUuid);
      count = r.data?.count ?? 0;
    } catch { /* 숫자를 못 받아도 삭제는 물어본다 */ }

    const msg = count > 0
      ? `영업기회 ${count}건의 소속이 풀립니다. 기회 자체는 지워지지 않습니다.\n계속할까요?`
      : '이 묶음을 지울까요?';
    if (!window.confirm(msg)) return;

    try {
      await groupApi.removeGroup(groupUuid);
      enqueueSnackbar('묶음을 지웠습니다.', { variant: 'success' });
      navigate('/chancemng/groups', { replace: true });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '지우지 못했습니다.', { variant: 'error' });
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }
  if (!group) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>묶음을 찾을 수 없습니다.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/chancemng/groups')}>목록으로</Button>
      </Box>
    );
  }

  const chances = group.chances || [];
  const money = (v) => (v ? getNumberComma(v) : '-');

  // 묶음 안의 접점을 한 번 모아 준다. 같은 거래처가 여러 건인 경우가 많다.
  const salesOwners = [...new Set(chances.map((c) => c.crtNm).filter(Boolean))];
  const customerContacts = [...new Set(
    chances.filter((c) => c.chargeNm).map((c) => `${c.partnerInc || ''} ${c.chargeNm}`.trim()),
  )];

  const columns = [
    {
      field: 'prjNo', headerName: '번호 · 사업명', flex: 1.5, minWidth: 220, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5, lineHeight: 1.4 }}>
          <Link component="button" variant="body2" sx={{ fontFamily: 'monospace' }}
                onClick={(e) => { e.stopPropagation(); navigate(`/go/${row.prjNo}`); }}>
            {row.prjNo}
          </Link>
          <Typography variant="caption" color="text.secondary" display="block">
            {row.cstNm}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'partnerInc', headerName: '거래처 · 담당자', flex: 1.5, minWidth: 240, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5, lineHeight: 1.4 }}>
          <Typography variant="body2">{row.partnerInc || '-'}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {[row.chargeNm, row.chargeTel, row.chargeEml].filter(Boolean).join(' · ') || '연락처 없음'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            영업 {row.crtNm || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'cstStatus', headerName: '단계', width: 100,
      renderCell: ({ value }) => (
        <Chip size="small" label={value || '-'}
              color={value === '수주' ? 'success' : value === '수주실패' ? 'default' : 'info'} />
      ),
    },
    {
      field: 'expSales', headerName: '예상 매출', width: 130, align: 'right', headerAlign: 'right',
      renderCell: ({ value }) => money(value),
    },
    {
      field: 'siDt', headerName: '계약완료일', width: 130,
      renderCell: ({ row }) => (row.delayYn === 'Y'
        ? <Chip size="small" color="warning" label={`${row.siDt} 지남`} />
        : <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.siDt || '—'}</span>),
    },
  ];

  return (
    <Box sx={{ m: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Button size="small" startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/chancemng/groups')}>목록</Button>
        <Typography variant="h6">{group.groupNm}</Typography>
        <Chip size="small" variant="outlined" label={group.groupTypNm || group.groupTyp} />
        <Chip size="small" label={group.groupStatus === 'CLOSED' ? '종료' : '진행'}
              color={group.groupStatus === 'CLOSED' ? 'default' : 'success'} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" onClick={() => setEditOpen(true)}>수정</Button>
        <Button size="small" variant="outlined" color="error" onClick={handleDelete}>삭제</Button>
      </Box>

      {(group.groupDesc || group.stDt || group.edDt) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {group.groupDesc}
          {(group.stDt || group.edDt) && ` · ${group.stDt || '…'} ~ ${group.edDt || '…'}`}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Stat label="영업기회" value={`${group.chanceCnt ?? 0}건`} />
        <Stat label="예상 매출" value={money(group.expSales)} />
        <Stat label="예상 매입" value={money(group.expBuy)} />
        <Stat label="예상 이익" value={money(group.expProfit)} />
        <Stat label="지연" value={`${group.delayCnt ?? 0}건`} warn={(group.delayCnt ?? 0) > 0} />
      </Box>

      {/* 묶음을 여는 다음 동작은 대개 "누구한테 연락하지" 다. 위에 한 번 모아 준다. */}
      {(salesOwners.length > 0 || customerContacts.length > 0) && (
        <Paper variant="outlined" sx={{ p: 1.3, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            이 묶음의 접점
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.3 }}>
            {salesOwners.length > 0 && (
              <Typography variant="body2">
                <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>영업</Typography>
                {salesOwners.join(' · ')}
              </Typography>
            )}
            {customerContacts.length > 0 && (
              <Typography variant="body2">
                <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>거래처</Typography>
                {customerContacts.join(' · ')}
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      <Divider sx={{ mb: 1 }} />

      <DataGrid
        rows={chances.map((c) => ({ id: c.prjUuid, ...c }))}
        columns={columns}
        autoHeight
        disableColumnMenu
        rowHeight={72}
        pageSizeOptions={[20, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        localeText={{ noRowsLabel: '아직 이 묶음에 들어온 영업기회가 없습니다.' }}
      />

      <GroupFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        group={group}
        onSaved={() => load()}
      />
    </Box>
  );
};

export default GroupDetailContainer;
